// NO MORE NEW TYPES IN THIS FILE
// This file is deprecated and will be removed in the future.
import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import type { AllocationDetails } from '@lemans/shared';
import { RouterReducerState, SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Action } from '@ngrx/store';
import { BadgeStatusColorsType, IBannerConfig, IInnerButton, ITooltipConfig } from '@ra-web-tech-ui-toolkit/components';
import { AppId, NavItem, UserNotification, UserProfile } from '@rockwell-automation-inc/common-utils';
import { CategoryName, CommandRequest } from '@servicesV2/command-request.service';
import { FlagScope } from '@servicesV2/harness.service';
import { PermsEvaluationResult } from '@servicesV2/permissions.service';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { ApiError } from '../v2/core/api-error';
import { AccessRequestState } from './access-requests';
import { InvitationState } from './invitation';
import { HubConnectionState } from './signalR/hub-connection-state';
import { TrialsState } from './trials/trials.model';
import { UiState } from './ui-state/store';
import { RoleNode, UserAccessState, UserRoleHierarchy } from './user-access';
import { UserMembership } from './user-data/user.model';

export type FeatureFlagStoreState = Partial<Record<FlagScope, FeatureFlag>>; //move to sub-state of control-page
export const featureFlagStoreInitialState: FeatureFlagStoreState = {
  'Common-Service': {},
  'Service-Flags': {},
};
export interface AppState {
  controlPage: PageControl;
  uiState: UiState;
  userData: UserPreferences;
  signalR: SignalRData; //move to sub-state of control-page
  featureFlags: FeatureFlagStoreState;
  trials: TrialsState;
  invitation: InvitationState;
  accessRequests: AccessRequestState;
  userAccess: UserAccessState;
  routerState: RouterReducerState<SerializedRouterStateSnapshot>;
}

export interface Version {
  apiVersion: string;
  portalVersion: string;
  notificationsVersion: string;
}
export type ApplicationMap = {
  [key in AppId | PreviewAppId]?: ApplicationCard;
};

export interface PageControl {
  versions?: Version;
  hasError?: boolean;
  isEulaAccepted: boolean;
  applications: ApplicationCard[];
  appsMap: ApplicationMap;
  enabledApplications: ApplicationCard[];
  isUserFound: Delayed<boolean>;
  isBaseURLSet: boolean;
  eulaConfig?: IEulaConfig;
  apiError?: ApiError;
  catalogInfo: Delayed<ICatalogInfo[]>;
  isUserActive: boolean;
  sessionTimedOut: boolean;
  extendSession: boolean;
  logoutSession: boolean;
  helpMenuToOpen?: NavItem;
  loggedInUserProfile?: UserProfile;
  // REVISIT: this routestate seems redundant since we also have route state ngrx in root store
  routeState: RouteState;
}

export interface Tenant {
  item1: string;
  item2: string;
}

export interface SignalRData {
  connectionState?: HubConnectionState;
  domainMessage?: IDomainMessage;
  lastError?: any;
}

export enum Role {
  Navigator = 'Navigator',
  Reader = 'Reader',
  Contributor = 'Contributor',
  Admin = 'Admin',
  Owner = 'Owner',
}

export enum SelectableRoles {
  Contributor = 'Contributor',
  Admin = 'Admin',
}

// ROLES ACCESSS
export const ROLES_WITH_MANAGE_ACCESS = [Role.Owner, Role.Admin];

const UNASSIGNABLE_ROLES = new Set([Role.Navigator, Role.Reader]);
export const ASSIGNABLE_ROLES = Object.values(Role).filter((role) => !UNASSIGNABLE_ROLES.has(role));

export interface UserInfo {
  userId: string;
  userName: string;
  userEmail: string;
  firstName: string;
  lastName: string;
}

export type ResourceRoleAction = Partial<UserRole> & {
  userId: string;
  userName: string;
  tenantId: string;
  action: 'add' | 'remove' | 'undoRemove';
};

// REVISIT: discuss with sagar
export enum ResourceType {
  Tenant = 'Tenant',
  Service = 'Service',
}

export type ResourceTypeString = keyof typeof ResourceType;

export type DisplayResourceType = 'Organization' | 'Sub-organization' | 'Service';
export interface Resource {
  resourceId: string;
  resourceName: string;
  resourceType: ResourceTypeString;
}
export type TenantRole = {
  role: Role;
  tenantId: string;
};
export type EffectiveRole = Resource & TenantRole;
export type UserRole = UserInfo &
  Resource &
  TenantRole & {
    accessRequestId: string;
  };
export interface UserPreferences {
  accessibleTenants?: AccessibleTenant[];
  businessPartnerId: string;
  company: string;
  email: string;
  eulaAcceptanceRequired: boolean;
  eulaContentUrl: string;
  eulaVersion: string;
  firstName: string;
  lastName: string;
  location: string;
  name: string;
  hasAnyServiceRole: Delayed<TenantAccessType>;
  preferences?: Preferences;
  effectiveRoles?: EffectiveRole[];
  resourceRoles?: UserRole[];
  userResourceRoles?: UserRole[];
  resources?: any;
  editUser?: UserRole;
  userId?: string;
  currentTenantId?: string;
  newTenantCreated?: boolean;
  currentTenantContext: Delayed<TenantContext>;
  currentTenant?: ITenantDetail;
  notificationCounts?: UserNotificationCounts;
  notifications?: UserNotification[];
  notificationDeleted?: UserNotification;
  notificationMarkedRead?: string;
  services: TenantService[];
  tenantUtilityTokens?: TenantUtilityTokens;
  currentCustomProvisioningData?: EntitlementData;
  exitFromProvisioning?: boolean;
  suggestedTenant?: ITenantDetail;
  selectedTenantUsers?: PageResponse<TenantUser>;
  noSuggestedTenant?: boolean;
  hasOrgEmailProvider: boolean;
  refreshUserEntitlementsGrid: boolean;
  refreshOrgEntitlementsGrid: boolean;
  refreshUserRolesGrid: boolean;
  unallocatedEntitlementsCount?: UnallocatedEntitlementCounts;
  allocateEntitlementResponse?: AllocateEntitlementResponse;
  manageableResources: Delayed<Resource[]>;
  userPermissionsEvalResult: Delayed<PermsEvaluationResult>;
  refreshTenantListGrid?: boolean;
  canChangeTenantHierarchy?: boolean;
  userMembership: UserMembership;
  userRoleHierarchy: Delayed<UserRoleHierarchy>;
  tenantResourceHierarchy: Delayed<TenantResourceHierarchy>;
}

export type TenantAccessType = 'Service' | 'SubOrgs';

export class Delayed<T> {
  private readonly status: 'pending' | 'loaded' = 'pending';
  private readonly data: T | null;

  constructor(data?: T) {
    if (data !== undefined && data != null) {
      this.status = 'loaded';
    }
    this.data = data ?? null;
  }

  get isPending(): boolean {
    return this.status === 'pending';
  }
  get isReady(): boolean {
    return this.status === 'loaded';
  }

  get value(): T {
    if (this.status === 'pending') {
      throw new Error('data is not loaded yet');
    }
    return this.data as T;
  }
}

export type ManageableResources = { status: 'initial' | 'loaded'; resources: Resource[] };

export interface TenantUser {
  userId: string;
  firstName: string;
  lastName: string;
  userName: string;
  userEmail?: string;
}

export interface CreateTenant {
  name: string;
  location: string;
  description: string;
  visibility: string;
  trialServices: string[];
}
export interface CreateChildTenant {
  parent: string;
  name: string;
  location: string;
  description: string;
  trialServiceKinds: string[];
}

export interface CreateTenantResponse {
  tenantId: string;
}

export interface ResponseBody<T> {
  body: T;
}

export interface Response<T> {
  response: ResponseBody<T>;
}

export interface UpdateTenant {
  name: string;
  tenantId: string;
  location: string;
  description: string;
  visibility: string;
  emailDomains?: string[];
}

export interface ApplicationCardStaticConfiguration {
  appDescription: string;
  appHub: string;
  appId: AppId | PreviewAppId;
  appImage: string;
  appName: string;
  hubLogo: string;
  isDefaultTrialEnabled?: boolean;
  badgeText?: BadgeTextConfig;
  dialogConfig?: {
    instructions: string;
    iconInstructions?: string;
    bannerArtwork: string;
    styleNameToSetDialogPaddingToZero?: string;
    actions?: ActionConfig[];
  };
  beingProvisioned?: boolean;
  provisionServiceId?: string;
  poweredBy?: string;
  isFavorite?: boolean;
  featureFlagName?: FlagName;
  supportsUniversalCredits?: boolean;
}

export enum BadgeTextConfig {
  TestersOnly = 'Testers Only',
  TrialAccess = 'Trial Access',
  PrivatePreview = 'Private Preview',
}

export interface ActionConfig {
  displayName: string;
  icon: string;
  applicationPath: string;
  queryParams: Map<string, string>;
}
export interface ApplicationCard extends ApplicationCardStaticConfiguration {
  appURL: string;
  provisioningUrl?: string;
}

export interface Preferences {
  lastAccessedTenantId?: string;
  disableTokenNotification?: boolean;
  // REVISIT: Why is this is a union
  favoriteApps?: FavoriteApps;
  language?: string;
}

export interface FavoriteApps {
  // REVISIT: move to appid?
  [key: string]: (AppId | PreviewAppId)[];
}

export interface AccessibleTenant {
  id: string;
  name: string;
  logoUrl?: string;
  shortName: string;
  parentId?: string;
}

export type UniversalCredits = 'UniversalCredits';

export enum PreviewAppId {
  Fiix = 'Fiix',
  Plex = 'Plex',
  FactoryTalkMotionAnalyzer = 'FactoryTalkMotionAnalyzer',
  Advisor = 'Advisor',
}

export function isPreviewApp(s: AppId | PreviewAppId | string): s is PreviewAppId {
  if (typeof s === 'string') {
    return PreviewAppId[s] !== undefined;
  }
  return Object.values(PreviewAppId).includes(s);
}

export type ArchiveTenantPayload = {
  tenantId: string;
  tenantName: string;
  isSubOrg?: boolean;
  parentId?: string;
};

export enum TenantStatus {
  Active = 'Active',
  Archived = 'Archived',
}

export type LeaveTenantPayload = {
  tenantId: string;
  tenantName: string;
  selectedTenantId: string;
  isLeavingCurrentTenant: boolean;
  isFromSettingsPage: boolean;
  tenantType: 'organization' | 'sub-organization';
  parentTenantId?: string;
};

export interface ITenantDetail {
  id: string;
  name: string;
  ownerName: string;
  ownerEmail?: string;
  owner?: string;
  location: string;
  inviteCode: string;
  description: string;
  emailDomain: string;
  emailDomains: string[];
  logoUrl: string;
  visibility: string;
  tenantStatus: TenantStatus;
  services?: Service[];
  disabledServices?: DisabledService[];
  pendingServices?: PendingService[];
  scheduledForActivationServices?: Service[];
  parent?: string;
}

export interface SelectedTenant {
  id: string;
  name: string;
  role: Role;
}

export type TenantContext = 'NotSelected' | 'Personal' | SelectedTenant;

export interface ICatalogInfo {
  catalogCode: string;
  name: string;
  description: string;
  serviceKind?: AppId;
  isCredit: boolean;
  isMockAllowed: boolean;
  get appName(): string;
}
export interface IEulaConfig {
  eulaVersion: string;
  eulaContentUrl: string;
  eulaContent: string;
}

export interface IUpdatedPreferences {
  preferences: IPreferenceKeyValue[];
}

export interface IPreferenceKeyValue {
  name: string;
  value: string;
}

// TO DO: verify this interface, doesn't match with response
export interface IApiResponse {
  status: boolean;
  error?: string;
}

export interface UserFeedback {
  feedback: string;
  feedbackType: string;
}

export interface NoContentApiResponse {
  [key: string]: any;
}

export interface NoContent {
  [key: string]: any;
}

export interface KeyValue {
  [key: string]: any;
}

export const NO_CONTENT: NoContent = {};

export interface PaginatedTableResponse<T> {
  records: T[];
  token: string | null;
}
export interface UserNotificationCounts {
  totalNotifications: number;
  unreadNotifications: number;
}

export type AnyObject = Record<string, unknown>;

export interface IDomainMessage {
  type: string;
  data: any;
  context: IContext;
  stream: string;
  get categoryName(): CategoryName | null;
}

export class DomainMessage implements IDomainMessage {
  type: string;
  data: any;
  context: IContext;
  stream: string;

  get categoryName(): CategoryName | null {
    if (!this.stream) {
      return null;
    }
    return this.stream.split('-')[0] as CategoryName;
  }

  public static create(val: any): IDomainMessage | null {
    if (Object.hasOwn(val, 'context')) {
      return new DomainMessage(val.type, val.data, val.context, val.stream);
    } else {
      return null;
    }
  }

  // only for unit test
  public static createMock(val: any): IDomainMessage {
    return new DomainMessage(val.type, val.data, val.context, val.stream);
  }

  private constructor(type: string, data: any, context: IContext, stream: string) {
    this.type = type;
    this.data = data;
    this.context = context;
    this.stream = stream;
  }
}

export function anyObjectTo<T>(input: AnyObject): T {
  return input as unknown as T;
}

export interface IContext {
  correlationId: string;
  causationId: string;
  userId: string;
  tenantId: string;
}

export interface PageRequest {
  tenantId?: string;
  perPage: number;
  pageNum: number;
  sortFilterContext?: SortFilterContext;
}

export interface TenantHierarchyUsersRequest extends Required<PageRequest> {
  includeTree: boolean;
  includeSiblings: boolean;
}

export interface PageResponse<T> {
  currentPage: number;
  records: T[];
  total: number;
}

export type LedgerRecord = {
  tenantId: string;
  userId: string;
  timestamp: Date;
  ts: Date;
  tokenCount: number;
  balance: number;
  userEmail: string;
};

export type AllocationDeallocationtRecord = LedgerRecord & {
  contractNumber: string;
  catalogNumber: string;
};

export type DebitRecord = LedgerRecord & {
  nantId: string;
  serviceKind: AppId;
  userId: string;
  duration: string;
  planId: string;
  reportWindow: Date;
  consumption: number;
  project?: string;
  resource?: string;
  unit: string;
  discountAmt?: number;
  discountPlans?: string;
  reasonCode?: string;
  startingBalance: number;
  tokensDeducted: number;
};

export enum Case {
  Credit = 'Credit',
  Debit = 'Debit',
  Adjustment = 'Adjustment',
  Deallocation = 'Deallocation',
}

export type CreditEntry = {
  Case: Case.Credit;
  Item: AllocationDeallocationtRecord;
  tokenConsumptionMapping?(): void;
};

export type DebitEntry = {
  Case: Case.Debit;
  Item: DebitRecord;
  tokenConsumptionMapping?(): void;
};

export type AdjustmentEntry = {
  Case: Case.Adjustment;
  Item: DebitRecord;
  tokenConsumptionMapping?(): void;
};

export type DeallocationEntry = {
  Case: Case.Deallocation;
  Item: AllocationDeallocationtRecord;
  tokenConsumptionMapping?(): void;
};

export type LedgerEntry = CreditEntry | DebitEntry | AdjustmentEntry | DeallocationEntry;

export interface LedgerRequest {
  tenantId: string;
  from: Date;
  duration: number;
  forExport: boolean;
  perPage?: number;
  token?: string | null;
}
export interface Entitlement {
  id: string;
  catalogNumber: string;
  contractNumber: string;
  status: string;
  name: string;
  description: string;
  serviceKind: AppId | '' | UniversalCredits;
  effectiveDate: Date;
  activationDate?: Date;
  expiryDate: Date;
  primaryEmail: string;
  consumed?: number;
  userId: string;
  tenantId: string;
  serviceId: string;
  fromHybris: boolean;
  quantity: number;
  isSystemGenerated: boolean;
  attributes: Attributes;
  available: number;
  timestamp: Date;
  allocated: number;
  isTrialEntitlement?: boolean;
  allocationDetails?: AllocationDetails[];
  isCustomProvisioning?: boolean;
}

export interface TokenEntitlement extends Entitlement {
  entitlementId: string;
}
export interface AGGridEntitlement extends Entitlement {
  dateToText(): void;
  allocateButtonToText(): void;
  detailsServiceDataToText(): void;
  detailServiceDataHeaders: { header: string; subheader: string };
  productFamily: string;
  entitlementType: string;
  availableQuantity?: string;
}

export enum EntitlementBadge {
  addon = 'Add-On',
  platform = 'Platform',
  additive = 'Additive',
  // oxlint-disable-next-line typescript/no-duplicate-enum-values
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  trial = 'Trial',
  utilityToken = 'Universal Credits',
  // oxlint-disable-next-line typescript/no-duplicate-enum-values
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  newtrial = 'Trial',
}

export interface Attributes {
  combineType: string;
  campaignId?: string;
}

export interface TenantUtilityTokens {
  id: string;
  tokenBalance: number;
  tokenExpiration: Date;
  tokenEntitlements: Entitlement[];
  disabledTokenEntitlements: Entitlement[];
}

export interface TenantUtilityTokensResponse {
  id: string;
  tokenBalance: number;
  tokenExpiration: Date;
  tokenEntitlements: TokenEntitlement[];
  disabledTokenEntitlements: TokenEntitlement[];
}

export interface AgGridState {
  refreshAgGrid?: boolean;
}

export enum ApplicationCardButtonAction {
  ADD = 'add',
  OPEN = 'open',
  PRODUCT_INFO = 'product-info',
  SETUP = 'setup',
  FAV = 'fav',
  REQUEST = 'request',
  TRANSFER_OWNERSHIP = 'Transfer ownership',
}

export enum TransferOwnershipType {
  APPLICATION = 'Application',
  ORGANIZATION = 'Organization',
}

export interface TileButton {
  label: string;
  id: ApplicationCardButtonAction;
  icon: string;
}

export interface ApplicationCardButton {
  button: TileButton;
  tooltipText: string;
  disabled?: boolean;
}

export interface ApplicationCardEvent {
  card: ApplicationCard;
  action: ApplicationCardButtonAction;
}

export interface LogoUrl {
  url: string;
}

export enum UploadLogoPreOperation {
  None = 'None',
  CreateTenant = 'CreateTenant',
  UpdateTenant = 'UpdateTenant',
  UpdateTenantName = 'UpdateTenantName',
}

export enum MessageIcons {
  Info = 'info-blue.svg',
  Success = 'check-circle-green.svg',
  Error = 'client-error-red.svg',
  Warning = 'warning-yellow.svg',
  LicenseScroll = 'license-scroll.svg',
}
export type ApproveUserRecord = Resource & {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  tenantId: string;
  created: string;
  status: string;
  error: string;
};

export interface ErrorContext {
  url: string;
  message: string;
  requestId?: string;
}

export interface AccessUser {
  userId: string;
  userName: string;
  userEmail: string;
  tenantId?: string;
}

export interface ServiceMapItem {
  appId: string;
  appName: string;
  serviceKind: AppId;
  hidden: boolean;
}

export interface Service {
  serviceId: string;
  kind: AppId;
  owner: string;
  entitlements: Entitlement[];
}

export interface DisabledService extends Service {
  disabledReason: string;
}

export interface TenantService extends Service {
  canAccess: boolean;
  role: Role;
  provisioned: boolean;
  pendingProvisioning: boolean;
  disabled: boolean;
  disablementReason: string;
}

export interface UserTenant {
  userId: string;
  tenantId: string;
}
export interface EntitlementDataState {
  pendingServices?: PendingService[];
}

export interface PendingService {
  serviceId: string;
  kind: AppId;
  entitlement: Entitlement;
}

export interface PendingServicePayload {
  serviceId: string;
  kind: {
    Case: string;
  };
}
export interface PendingServiceV2 {
  service: PendingService;
  transaction: EntitlementTransactionInfo;
  userId: string;
}
export interface EntitlementTransactionInfo {
  entitlementId: string;
  entitlement: Entitlement;
  userId: string;
  tenantDetails: TenantAttributesDto;
  quantity: number;
  timestamp: Date;
}
export interface TenantAttributesDto {
  tenantId: string;
  isPersonal: boolean;
}
export interface EntitlementData {
  entitlement: Entitlement;
  app?: ApplicationCard;
  serviceId?: string;
  tenantId: string;
  userId: string;
  continue?: boolean;
}

export interface EntitlementCounts {
  serviceKind: AppId;
  supportsCredits: boolean;
  unallocatedCredits: number;
  unallocatedServices: number;
  totalEntitlements: number;
}

export interface UnallocatedEntitlementCounts {
  entitlementCounts: EntitlementCounts[];
  totalCreditsEntitlementCount: number;
  totalEntitlementCount: number;
}

export interface BadgeInfo {
  color: string;
  text: string;
}

export const TOKEN_BALANCE_FRACTION_DIGITS = 3;

export enum EntitlementActionOptions {
  Full = 'Full',
  Partial = 'Partial',
}

export enum EntitlementAllocationModalDisplayModes {
  allocate = 'allocate',
  review = 'review',
}

export enum EntitlementDeallocationModalActions {
  Cancel = 'Cancel',
  DeallocateEntitlement = 'Deallocate Entitlement',
}

export interface AgGridEntitlementColumnsFormat {
  header: string;
  subheader: string;
}

export interface CommandRequestData {
  commandRequest: CommandRequest;
  response: HttpResponse<object>;
}
export interface ICommandResponse<T = any> {
  response?: HttpResponse<T>;
  message?: IDomainMessage;
  error?: ApiError;
  errorActions?: Action[];
}

export enum MultiRequestErrorState {
  NO_ERRORS,
  PARTIAL_ERRORS,
  ALL_ERRORS,
}

export type UserId = string & { isGuid: true };
export function toUserId(userId: string): UserId {
  return userId as UserId;
}

export type FlagValue = boolean | string | number | object | undefined;
export type FlagName =
  | 'allow_fake_entitlements'
  | 'enable_language_switcher'
  | 'enable_tech_support'
  | 'enable_theme_switcher'
  | 'maintenance_window'
  | 'pending_api_support'
  | 'tile_advisor'
  | 'tile_assetcenter'
  | 'tile_elementary'
  | 'tile_mepm'
  | 'tile_motion_analyzer'
  | 'tile_ppax_analytics'
  | 'enable_credits_banner'
  | 'ui_suppress_signalr_pinger'
  | 'deallocatable_ent_sks'
  | 'enable_portalv2_user_management'
  | 'enable_user_assigned_trials';

export interface FeatureFlagUpdate {
  flag: FlagName;
  value: FlagValue;
}
export type FeatureFlag = Record<string, FlagValue>;

export type HarnessIdentifier = UserId | 'common-service-portal';

export interface FeatureFlagTarget {
  identifier: HarnessIdentifier;
  attributes: {
    CurrentTenantId?: string;
    CurrentTenantName?: string;
    // choice type for target
    Type: 'User' | 'Application';
    Domain?: string;
  };
}

export interface AllRecordsRequest {
  url: string;
  perPage?: number;
  httpParams?: HttpParams;
  headers?: HttpHeaders;
}

export type FilterType =
  | 'greaterThanOrEqual'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'lessThan'
  | 'equals'
  | 'notEqual'
  | 'inRange'
  | 'contains';

export type FilterContext = {
  filterType: 'set' | 'number' | 'text' | 'date';
  type?: FilterType;
  filter?: any;
  filterTo?: any;
  dateTo?: Date;
  dateFrom?: Date;
  values?: string[];
};

export type FilterModel = {
  [key: string]: FilterContext;
};

export type AgFilterContext = {
  filterType: 'set' | 'number' | 'text' | 'date';
  type?: FilterType;
  filter?: any;
  filterTo?: any;
  dateTo?: string;
  dateFrom?: string;
  values?: (string | null)[];
};

export type AgFilterModel = {
  [key: string]: AgFilterContext;
};

export type SortModel = {
  colId: string;
  sort: 'asc' | 'desc';
};
export interface SortFilterContext {
  sortModel: SortModel[];
  filterModel: FilterModel;
}

export interface ErrorMessageMap {
  [errorCode: string]: string;
}

export interface IMaintenanceWindow {
  from: DateTime;
  duration: number;
  /// optional custom message shown alongside the maintenance window message (e.g. EULA update notice)
  customMessage?: string;
  get to(): DateTime;
  get isActive(): boolean;
  startsWithinDays(days: number): boolean;
}

/**
 * Contract for the `deallocatable_ent_sks` Harness feature flag, which controls manual (front-end only)
 * deallocation of Active entitlements. Pending entitlement behavior and system/automatic deallocation are
 * never affected by this flag. Entitlement status, Universal Credits aside from the pass-through below, and
 * tenant-permission checks are the caller's responsibility, not this manager's.
 *
 * Supported raw flag values:
 * - `"ALL"` — deallocation is enabled for every service kind, with no allocated-date threshold.
 * - `"NONE"` — deallocation is disabled for every service kind.
 * - A JSON array of per-service-kind configuration objects, each shaped as:
 *   ```
 *   { "serviceKind": "<AppId string, e.g. ftoptix>", "enableDeallocationAfter": "<ISO 8601 date-time, optional>" }
 *   ```
 *   Example:
 *   ```json
 *   [
 *     { "serviceKind": "ftoptix", "enableDeallocationAfter": "2026-10-10T17:34:00+05:30" },
 *     { "serviceKind": "secureremoteaccess", "enableDeallocationAfter": "2026-10-10T17:34:00+05:30" },
 *     { "serviceKind": "ide", "enableDeallocationAfter": "2026-10-10T17:34:00+05:30" },
 *     { "serviceKind": "datamosaix" }
 *   ]
 *   ```
 *   - `enableDeallocationAfter` must be an ISO 8601 date-time string. Values without a timezone offset are
 *     interpreted in the browser's local timezone; `Z` and explicit offsets (`+HH:MM`/`-HH:MM`) are also
 *     supported. The timestamps are compared as complete instants; an allocation at or after the threshold
 *     is eligible.
 *   - Each service kind's threshold is configured independently; different service kinds may use different
 *     threshold dates.
 *   - When `enableDeallocationAfter` is omitted/blank for a configured service kind, any allocation date
 *     satisfies the check for that service kind (legacy behavior is preserved). A threshold that is present
 *     but malformed/unparseable fails closed (blocks deallocation) for that service kind.
 *   - Legacy flag values (e.g. a bare JSON array of service-kind strings) are no longer supported and are
 *     treated as `NONE`.
 *
 * The threshold is evaluated per Allocation ID (using the allocation's allocated date) for entitlement units
 * that support individual deallocation, and is also applied to the full-quantity deallocation action for
 * Active entitlements whose custom provisioning is incomplete (using the entitlement's allocation date).
 */
export interface IEntitlementDeallocationFlagManager {
  /// Evaluates Harness feature enablement and, where applicable, the allocated-date threshold for a service
  /// kind.
  ///
  /// `isCustomProvisioningPending` distinguishes the two call sites:
  /// - Omitted (per-Allocation ID calls): the configured threshold, if any, is always evaluated against
  ///   `allocationDate`.
  /// - `false` (ordinary Active entitlements): only service-kind enablement is checked; the threshold is not
  ///   applied.
  /// - `true` (full-quantity action on an entitlement with incomplete custom provisioning): the configured
  ///   threshold, if any, is evaluated against `allocationDate`.
  ///
  /// Compares complete timestamp instants (at or after the threshold is eligible). Returns false when the
  /// service kind is not deallocation-enabled, when its configured threshold is present but malformed, or when
  /// a required `allocationDate` is invalid. Returns true when the threshold is empty/blank (no threshold
  /// configured for that service kind).
  ///
  /// Entitlement status, Universal Credits, and tenant-permission checks are the caller's responsibility; this
  /// method must only be called with a resolved `AppId` service kind.
  canDeallocate(serviceKind: AppId, allocationDate: Date, isCustomProvisioningPending?: boolean): boolean;
}

export interface CreateTenantSuccess {
  newTenantId: string;
  blobImage: Blob;
}

export enum HasCredits {
  'with-credits' = 'With credits',
  'without-credits' = 'Without credits',
}

// REVISIT: does not contain any flow for join org?
// can probably be removed based on refactor in
// control page
export enum AppFlow {
  Dashboard = 'Dashboard',
  Invitation = 'Invitation',
  RedeemTrial = 'RedeemTrial',
  JoinOrgRequest = 'JoinOrgRequest',
  Entitlement = 'Entitlement',
}
export interface RouteState {
  /// REVISIT: if this `flow` goes away then it removes a reason to duplicate this
  flow: AppFlow;
  snapshot?: SerializedRouterStateSnapshot;
}

export enum BannerType {
  MaintenanceWindow = 'MaintenanceWindow',
  NegativeTokenBalance = 'NegativeTokenBalance',
  SessionTimeout = 'SessionTimeout',
  TenantArchived = 'TenantArchived',
  SignalRDisconnected = 'SignalRDisconnected',
  SubOrgInfo = 'SubOrgInfo',
  UnsavedChanges = 'UnsavedChanges',
}

export const DefaultLanguage = 'en';

export enum LoginErrorCode {
  MissingIdentity = 'MissingIdentity',
  MissingConnectionClaim = 'MissingConnectionClaim',
  MissingUniqueIdentifierClaim = 'MissingUniqueIdentifierClaim',
  RootClaimMissing = 'RootClaimMissing',
  InvalidConnectionClaim = 'InvalidConnectionClaim',
  InvalidMergeCandidates = 'InvalidMergeCandidates',
  PreApprovalCheckFailed = 'PreApprovalCheckFailed',
  MissingEmailClaim = 'MissingEmailClaim',
  MissingGivenNameClaim = 'MissingGivenNameClaim',
  MissingFamilyNameClaim = 'MissingFamilyNameClaim',
  DemoUserDisabled = 'DemoUserDisabled',
  ReservationHasExpired = 'ReservationHasExpired',
  ReservationNotFound = 'ReservationNotFound',
  Unknown = 'Unknown',
}
export interface LoginError {
  errorCode: LoginErrorCode;
  errorDescription: string;
  email: string;
}

export type Env = 'sandbox' | 'non-prod' | 'pre-prod' | 'demo' | 'production' | 'local';

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}
export interface CurrentAllocatedEntitlement {
  catalogNumber: string;
  entitlementId: string;
  effectiveDate: string;
  expiryDate: string;
  pending: boolean;
}

export interface ProposedEntitlement {
  catalogNumber: string;
  entitlementId: string;
  effectiveDate: string;
  expiryDate: string;
  pending: boolean;
}

export interface OverlapConflict {
  case: PlatformExpectationCase;
  entitlementId: string;
  futureStartDate?: string;
  startDate?: string;
  overlappingExpiryDate?: string;
  until?: string;
  from?: string;
}

export interface AllocateEntitlementResponse {
  info: {
    current: CurrentAllocatedEntitlement[];
    proposed: ProposedEntitlement;
    conflict: OverlapConflict;
  };
  message: string;
  outcome: AllocateEntitlementOutcome;
}

export interface AllocateEntitlementPayload {
  quantity: number;
  platformExpectation: {
    case: PlatformExpectationCase;
    entitlementId?: string;
    userChoice?: string;
  };
}

export enum AllocateEntitlementOutcome {
  PlatformDeallocateFirst = 'PlatformDeallocateFirst',
  AddonPlatformUnsatisfied = 'AddonPlatformUnsatisfied',
  AddonPlatformOverlapUnsatisfied = 'AddonPlatformOverlapUnsatisfied',
  QuantityExceedsAvailable = 'QuantityExceedsAvailable',
  PlatformExpectationMismatch = 'PlatformExpectationMismatch',
  ServiceProvisioningInProgress = 'ServiceProvisioningInProgress',
  ConcurrentAllocationInProgress = 'ConcurrentAllocationInProgress',
  Invalid = 'Invalid',
  TenantIsArchived = 'TenantIsArchived',
  FirstEntitlementOfCSPServiceMustBeActive = 'FirstEntitlementOfCSPServiceMustBeActive',
}

export enum CreateSubOrganizationOutcome {
  ParentHasParent = 'ParentHasParent',
  TenantNotFound = 'TenantNotFound',
  TenantArchived = 'TenantArchived',
  ParentLocked = 'ParentLocked',
  ParentCannotBePersonalTenant = 'ParentCannotBePersonalTenant',
  Started = 'Started',
}

export enum PlatformExpectationCase {
  NoConflict = 'NoConflict',
  AsapOrDefer = 'AsapOrDefer',
  Asap = 'Asap',
  GapAfterCurrent = 'GapAfterCurrent',
  Replaces = 'Replaces',
  Defer = 'Defer',
}

export enum DashboardSections {
  PENDING_FOR = 'Pending for',
  FAVORITES = 'Favorites',
  FOR_YOU = 'For you',
  BY_REQUEST = 'By request',
  MORE_FROM_ROCKWELL_AUTOMATION = 'More from Rockwell Automation',
}

export interface CardsBadgeMapConfig {
  value: '!' | number;
  config: {
    color: BadgeStatusColorsType;
    cap: number;
    tooltipText: string;
    tooltipConfig: ITooltipConfig;
  };
}

export interface Banner {
  show: boolean;
  message: string;
  config: IBannerConfig;
  buttons: IInnerButton[];
  raIcon: string;
  id: string;
}

export interface NavigationGuardComponent {
  confirmNavigation$(): Observable<boolean>;
}
export interface TenantUserAccess {
  user: TenantUser;
  access: RoleNode;
}

export interface ServiceInfo {
  id: string;
  kind: AppId;
  enabled: boolean;
}
export interface TenantResourceHierarchy {
  id: string;
  name: string;
  parentId: string | null;
  provisionedServices: ServiceInfo[];
  children: TenantResourceHierarchy[];
}
